/* eslint-disable import/no-unused-modules */
import {
  Button,
  ButtonGroup,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Toggle,
} from '@nike/eds';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import * as R from 'ramda';
import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router-dom';
import { MultiValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDebouncedCallback } from 'use-debounce';
import {
  rewriteEditModalAtom,
  rewriteUpdateHasErrorAtom,
  rewriteUpdateMessageAtom,
  selectedRewriteAtom,
} from '../../atoms/rewrite';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import RewriteClient from '../../clientAPI/rewrite';
import TagsInput from '../../components/RewriteTypoShared/TagsInput';
import {
  QUERY_KEYS,
  RELATION_TYPE,
  REWRITE_T2C_TARGET_SOURCE,
  REWRITE_TYPE,
} from '../../constants';

export default function TermItemEdit({ type }: { type: 'rewrite' }) {
  const queryClient = useQueryClient();
  const [visible, setVisible] = useAtom(rewriteEditModalAtom);
  const [updateMsg, setUpdateMsg] = useAtom(rewriteUpdateMessageAtom);
  const [updateHasError, setUpdateHasError] = useAtom(
    rewriteUpdateHasErrorAtom,
  );
  const [rewrite] = useAtom(selectedRewriteAtom);
  const [textValue, setTextValue] = useState((rewrite?.target as string) || '');
  const [enabled, setEnabled] = useState<boolean>(rewrite?.enabled || true);
  const [rewriteType, setRewriteType] = useState(REWRITE_TYPE.T2T);
  const [relationType, setRelationType] = useState(
    rewrite?.relationType || RELATION_TYPE.AND,
  );
  const [includeOriginal, setIncludeOriginal] = useState<boolean>(
    rewrite?.includeOriginal || true,
  );
  const [selectedConceptOpts, setSelectedConceptOpts] = useState<
    { value: any; label: string }[]
  >([]);

  const [rewriteTagsErrorMessage, setRewriteTagsErrorMessage] = useState('');

  const [rewriteTags, setRewriteTags] = useState<RewriteTag[]>([]);

  const handleRewriteTagsChange = (newTags: RewriteTag[]) => {
    setRewriteTags(newTags);
  };

  // const [resetKey, setResetKey] = useState(0);
  const [resetKey, setResetKey] = useState(Date.now());

  const revalidator = useRevalidator();
  const queryKey = QUERY_KEYS.REWRITE_LIST;
  const source = REWRITE_T2C_TARGET_SOURCE;
  const apiClient = RewriteClient;
  const updateMutation = useMutation({
    mutationFn: (payload: RewriteUpdatePayload): Promise<RewriteItem> => {
      return apiClient.update(payload._id as string, R.omit(['_id'], payload));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
      setUpdateHasError(false);
      setUpdateMsg('Saved!');
    },
    onError: (err: keyable) => {
      setUpdateMsg(err.message || JSON.stringify(err));
      setUpdateHasError(true);

      err.status === 401 && revalidator.revalidate();
    },
  });
  const delMutation = useMutation({
    mutationFn: (id: string) => {
      return apiClient.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
      setUpdateHasError(false);
      setUpdateMsg('Deleted!');
    },
    onError: (err: keyable) => {
      setUpdateMsg(err.message || JSON.stringify(err));
      setUpdateHasError(true);

      err.status === 401 && revalidator.revalidate();
    },
  });

  const resetModal = () => {
    // setResetKey(prevKey => prevKey + 1);
    setResetKey(Date.now());
  };

  const onDismissModal = () => {
    resetModal();
    setVisible(false);
  };

  const onUpdateBtnClick = () => {
    const payload = {
      enabled: enabled,
      _id: rewrite?._id,
      type: rewriteType,
      target:
        rewriteType === REWRITE_TYPE.T2T
          ? textValue
          : selectedConceptOpts.map((opt) => ({
              id: opt.value.id,
              parentId: opt.value.parentId,
              name: opt.value.name,
              source,
            })),
      rewriteTags: rewriteTags,
    };
    if (rewriteType === REWRITE_TYPE.T2C) {
      (payload as RewriteUpdatePayload).relationType = relationType;
    }
    if (type === 'rewrite') {
      (payload as RewriteUpdatePayload).includeOriginal = includeOriginal;
    }
    const hasValidRewriteTag = rewriteTags.some((rt) =>
      ['normal', 'typo'].includes(rt.tagName.toLowerCase()),
    );

    const hasNormalTag = rewriteTags.some(
      (rt) => rt.tagName.toLowerCase() === 'normal',
    );
    const hasTypoTag = rewriteTags.some(
      (rt) => rt.tagName.toLowerCase() === 'typo',
    );

    // Check for the presence of both 'normal' and 'typo' tags
    const hasConflictingTags = hasNormalTag && hasTypoTag;

    if (rewriteTags.length === 0 || !hasValidRewriteTag) {
      return setRewriteTagsErrorMessage(
        'Required, At least set rewriteTag to "normal" or "typo"!',
      );
    } else if (hasConflictingTags) {
      // If both 'normal' and 'typo' tags are present, set an error message
      return setRewriteTagsErrorMessage(
        'Invalid configuration: A rewrite cannot be both "normal" and "typo"',
      );
    }
    updateMutation.mutate(payload as RewriteUpdatePayload);
    onDismissModal();
  };
  const onDeleteBtnClick = () => {
    delMutation.mutate(rewrite?._id as string);
    onDismissModal();
  };

  const asyncOptions = useDebouncedCallback((inputValue, callback) => {
    RecommendConceptsClient.searchConcepts(inputValue)
      .then((res) => {
        const opts = res.objects.map((c) => {
          const label =
            c.name === c.alternateName
              ? `${c.name} ${c.source.system}`
              : `${c.name}(${c.alternateName}) ${c.source.system}`;
          return {
            value: {
              id: c.id,
              parentId: c.parentId,
              name: c.name,
              source,
            },
            label,
          };
        });
        callback(opts);
      })
      .catch((err: keyable) => {
        console.log(err);
        err.status === 401 && revalidator.revalidate();
        callback([]);
      });
  }, 1000);

  const handleSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConceptOpts = R.uniqBy(
      (t) => t.value.id,
      newSelectOpts.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    );
    setSelectedConceptOpts(selectedConceptOpts);
  };

  useEffect(() => {
    if (rewrite?.type === REWRITE_TYPE.T2T) {
      setRewriteType(REWRITE_TYPE.T2T);
      setTextValue(rewrite.target as string);
    } else if (rewrite?.type === REWRITE_TYPE.T2C) {
      setRewriteType(REWRITE_TYPE.T2C);
      setSelectedConceptOpts([]);
    }
    setEnabled(rewrite?.enabled as boolean);
    setRelationType(rewrite?.relationType || RELATION_TYPE.AND);
    setIncludeOriginal(rewrite?.includeOriginal as boolean);
  }, [rewrite]);

  useEffect(() => {
    if (rewrite?.type === REWRITE_TYPE.T2C) {
      const conceptQueries = (
        rewrite?.target as unknown as RewriteTarget[]
      ).map((c) =>
        RecommendConceptsClient.getConceptById(c.id).catch((err) => {
          console.log(err);
          err.status === 401 && revalidator.revalidate();
          return null;
        }),
      );
      Promise.all(conceptQueries).then((result) => {
        const opts = result.map((data, idx) => {
          if (data) {
            const label =
              data.name === data.alternateName
                ? `${data.name} ${data.source.system}`
                : `${data.name}(${data.alternateName}) ${data.source.system}`;
            return {
              value: data,
              label,
            };
          } else {
            const concept = rewrite?.target[idx] as RewriteTarget;
            return {
              value: {
                id: concept.id,
                parentId: concept.parentId,
                name: concept.name,
                source,
              },
              label: concept.name,
            };
          }
        });
        setSelectedConceptOpts(opts);
      });
    }
  }, [rewrite]);

  return (
    <Modal
      onDismiss={onDismissModal}
      isOpen={visible}
      headerSlot={'Edit Term Rewrite'}
    >
      <div
        className='tw-flex tw-flex-col tw-justify-center'
        style={{ width: '40rem' }}
      >
        <div className='tw-mb-10 tw-pb-20'>
          <TextField
            type='text'
            value={rewrite?.term}
            id={`rewrite-term-edit_${rewrite?._id}`}
            placeholder='term'
            label=''
            subtitle=''
            readOnly={true}
          />
          <RadioGroup
            id='rewrite-type-edit'
            label=''
            orientation='horizontal'
            name={`radio-button-group-rewrite-edit_${rewrite?._id}`}
            valueSelected={rewriteType}
            onChange={(event) => {
              const { target } = event;
              const val = (target as HTMLInputElement).value;
              setRewriteType(val);
              setSelectedConceptOpts([]);
              setTextValue('');
            }}
          >
            <Radio
              label='Text To Text'
              value={REWRITE_TYPE.T2T}
              id={`rewrite-type-1-edit_${rewrite?._id}`}
            />
            <Radio
              label='Text To Concepts'
              value={REWRITE_TYPE.T2C}
              id={`rewrite-type-2-edit_${rewrite?._id}`}
            />
          </RadioGroup>
          {rewriteType === REWRITE_TYPE.T2T && (
            <TextField
              type='text'
              value={textValue}
              onChange={(event) => {
                const { target } = event;
                const val = (target as HTMLInputElement).value;
                setTextValue(val);
              }}
              id={`rewrite-text-target-edit_${rewrite?._id}`}
              placeholder='target term'
              label=''
              subtitle=''
            />
          )}
          {rewriteType === REWRITE_TYPE.T2C && (
            <div className='eds-select tw-mt-4'>
              <AsyncSelect
                className='eds-text-field__input eds-text-field--select'
                classNamePrefix='eds-select'
                isMulti
                cacheOptions
                value={selectedConceptOpts}
                loadOptions={asyncOptions}
                onChange={handleSelectChange}
              />
              <RadioGroup
                id='relation-type-edit'
                label=''
                orientation='horizontal'
                name={`radio-button-group-relation-type-edit_${rewrite?._id}`}
                valueSelected={relationType}
                onChange={(event) => {
                  const { target } = event;
                  const val = (target as HTMLInputElement).value;
                  setRelationType(val);
                }}
              >
                <Radio
                  label='AND'
                  value={RELATION_TYPE.AND}
                  id='relation-type-1-edit'
                />
                <Radio
                  label='OR'
                  value={RELATION_TYPE.OR}
                  id='relation-type-2-edit'
                />
              </RadioGroup>
            </div>
          )}
          {type === 'rewrite' && (
            <div className='eds-select tw-mt-4'>
              <Toggle
                label='Include Original Term'
                id={`toggle-include-original_${rewrite?._id}`}
                checked={includeOriginal}
                onClick={() => setIncludeOriginal(!includeOriginal)}
              />
            </div>
          )}
          <div className='eds-select tw-mt-4'>
            <Toggle
              id={`≈${rewrite?._id}`}
              label='Enabled'
              checked={enabled}
              onClick={() => setEnabled(!enabled)}
            />
          </div>
          <TagsInput
            onRewriteTagsChange={handleRewriteTagsChange}
            resetKey={resetKey}
            parentComponentName={'termItemEdit'}
            rewriteTagsErrorMessage={rewriteTagsErrorMessage}
            setRewriteTagsErrorMessage={setRewriteTagsErrorMessage}
          />
        </div>
        <ButtonGroup>
          <Button
            onClick={onUpdateBtnClick}
            size='small'
            variant='secondary'
            className='eds-background--blue-50 eds-color--white eds-border--blue-50'
          >
            Update
          </Button>
          <Button
            onClick={onDeleteBtnClick}
            size='small'
            variant='secondary'
            className='eds-background--red-70 eds-color--white eds-border--red-70'
          >
            Delete
          </Button>
          <Button onClick={onDismissModal} size='small' variant='secondary'>
            Cancel
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  );
}
